import { Box, useTheme } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import React, { useMemo, useState } from "react";
import { activeProfileAtom } from "../../../atoms/user/activeProfileAtom";
import { isActiveUserEngineerStudioManagerOrArtistAtom } from "../../../atoms/user/activeUserAtom";
import { useAppSelector } from "../../../store/hooks";
import { ResponsiveDialog } from "../../core-ui/components/ResponsiveDialog/ResponsiveDialog";
import { Text } from "../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../core-ui/components/Text/TextUtils";
import {
  EETab,
  EETabs,
  TabsBottomGreyBar,
} from "../../screens/ProfileScreen/ProfileScreen.styles";
import { SERVICE_FORM_MODAL_FOOTER_ID } from "../Navigation/BottomNav/useBottomTabBarOverlayView";
import { accountSettingsModalOpenAtom } from "./atoms";
import { ACCOUNT_SETTINGS_MODAL_ID } from "./constants";
import {
  a11yProps,
  AccountSettingsModalTabs,
  accountSettingsTabs,
} from "./utils";

interface AccountSettingsModalProps {
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{ width: "100%" }}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: 3 }}>{children}</Box>}
    </div>
  );
};

export const AccountSettingsModal = ({
  onClose,
}: AccountSettingsModalProps) => {
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.accountInfo);
  const isEngineerOrStudioManagerOrArtist = useAtomValue(
    isActiveUserEngineerStudioManagerOrArtistAtom,
  );
  const activeProfile = useAtomValue(activeProfileAtom);
  const displayName = useMemo(
    () => activeProfile?.display_name || `@${activeProfile?.username}`,
    [activeProfile?.display_name, activeProfile?.username],
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    AccountSettingsModalTabs.AccountDetails,
  );
  const [isAccountSettingsModalOpen, setIsAccountSettingsModalOpen] = useAtom(
    accountSettingsModalOpenAtom,
  );

  const handleSetSelectedTabIndex = (
    _: React.SyntheticEvent,
    tabIndex: number,
  ) => {
    setSelectedTabIndex(tabIndex);
  };

  if (!user) {
    return null;
  }

  return (
    <ResponsiveDialog
      open={isAccountSettingsModalOpen}
      onClose={() => setIsAccountSettingsModalOpen(false)}
      titleProps={{
        children: `Account Settings for ${displayName}`,
      }}
      id={ACCOUNT_SETTINGS_MODAL_ID}
      maxWidth={"md"}
      fullWidth
      hideCloseAction={true}
      actionsProps={{
        id: SERVICE_FORM_MODAL_FOOTER_ID,
        children: <></>,
        sx: {
          display: "flex",
          justifyContent: "center",
        },
      }}
      contentProps={{
        dividers: false,
        sx: {
          padding: 0,
        },
        children: (
          <>
            <Box
              px={2}
              boxSizing={"border-box"}
              sx={(theme) => ({
                zIndex: theme.zIndex.appBar,
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.default,
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              })}
              overflow={"hidden"}
            >
              <TabsBottomGreyBar />
              <EETabs
                value={selectedTabIndex}
                onChange={handleSetSelectedTabIndex}
                aria-label="Account Settings Navigation Tab"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {accountSettingsTabs.map((tab, index) => {
                  if (
                    tab.value === AccountSettingsModalTabs.PaymentAndBilling &&
                    !isEngineerOrStudioManagerOrArtist
                  ) {
                    return null;
                  }
                  const tabLabel = (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Text
                        variant={TextStyleVariant.P1}
                        style={{
                          color:
                            selectedTabIndex === tab.value
                              ? theme.palette.text.primary
                              : theme.palette.standardColor.Gray[400],
                        }}
                        bold
                      >
                        {tab.label}
                      </Text>
                    </Box>
                  );

                  return (
                    <EETab
                      key={tab.value}
                      label={tabLabel}
                      {...a11yProps(index)}
                    />
                  );
                })}
              </EETabs>
            </Box>
            <Box
              sx={{
                overflowY: "auto",
                width: "100%",
                boxSizing: "border-box",
              }}
              boxSizing={"border-box"}
              px={2}
              pb={2}
            >
              {accountSettingsTabs.map((tab) => (
                <TabPanel
                  key={tab.value}
                  value={selectedTabIndex}
                  index={tab.value}
                >
                  {tab.content(onClose)}
                </TabPanel>
              ))}
            </Box>
          </>
        ),
      }}
    />
  );
};
